import { type CSSProperties, type RefCallback, useRef } from "react";
import { useUnsafeLayoutEffect } from "../../hooks/use-unsafe-layout-effect.ts";
import {
    blendAcross,
    EDGE_BLEND_PX,
    type EdgeTint,
    type Hex,
    mixHex,
} from "./edge-color.ts";

export type EdgePalette = { top: Hex; bottom: Hex };

export type EdgeLayer = "route" | "page";

const LAYER_RANK: Record<EdgeLayer, number> = { route: 0, page: 1 };

const EDGE_PROPERTIES = [
    "--edge-top-background",
    "--edge-bottom-background",
] as const;
const ROOT_FLAG = "data-browser-edges";
const OVERLAY_SETTLE_MS = 600;
const EDGE_ROW_PX = 12;

export function viewportTop(element: Element | null | undefined): number {
    return element?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY;
}

export function blendAtTop(
    from: Hex,
    to: Hex,
    element: Element | null | undefined,
): Hex {
    return blendAcross(from, to, EDGE_BLEND_PX - viewportTop(element));
}

type Overlay =
    | { kind: "scrim"; element: HTMLElement; tint: EdgeTint }
    | { kind: "panel"; element: HTMLElement; color: Hex };

const overlays = new Set<Overlay>();
const overlayRefs = new Map<string, RefCallback<HTMLElement>>();

function inDocumentOrder(a: Overlay, b: Overlay): number {
    return a.element.compareDocumentPosition(b.element) &
        Node.DOCUMENT_POSITION_FOLLOWING
        ? -1
        : 1;
}

function isShown(element: HTMLElement): boolean {
    if ("checkVisibility" in element) {
        return element.checkVisibility({ visibilityProperty: true });
    }
    const style = getComputedStyle(element);
    return style.display !== "none" && style.visibility !== "hidden";
}

function withOverlays(palette: EdgePalette): EdgePalette {
    let { top, bottom } = palette;
    const ordered = [...overlays].sort(inDocumentOrder);
    for (const overlay of ordered) {
        if (overlay.kind !== "scrim" || !isShown(overlay.element)) continue;
        const { element, tint } = overlay;
        const strength = tint.alpha * Number(getComputedStyle(element).opacity);
        if (!(strength > 0)) continue;
        top = mixHex(top, tint.color, strength);
        bottom = mixHex(bottom, tint.color, strength);
    }
    for (const overlay of ordered) {
        if (overlay.kind !== "panel") continue;
        const { element, color } = overlay;
        const rect = element.getBoundingClientRect();
        const strength = Number(getComputedStyle(element).opacity);
        if (rect.top <= 0 && rect.bottom > EDGE_ROW_PX) {
            top = mixHex(top, color, strength);
        }
        if (
            rect.top < window.innerHeight - EDGE_ROW_PX &&
            rect.bottom >= window.innerHeight - 1
        ) {
            bottom = mixHex(bottom, color, strength);
        }
    }
    return { top, bottom };
}

type EdgeOwner = { layer: EdgeLayer; paletteAt: () => EdgePalette };

const owners: EdgeOwner[] = [];
let frame = 0;
let followUntil = 0;
let detach: (() => void) | null = null;
let themeColor: HTMLMetaElement | null = null;

function currentOwner(): EdgeOwner | undefined {
    let current: EdgeOwner | undefined;
    for (const owner of owners) {
        if (!current || LAYER_RANK[owner.layer] >= LAYER_RANK[current.layer]) {
            current = owner;
        }
    }
    return current;
}

function paint() {
    cancelAnimationFrame(frame);
    frame = 0;
    const owner = currentOwner();
    if (!owner) return;
    const { top, bottom } = withOverlays(owner.paletteAt());
    const style = document.documentElement.style;
    if (style.getPropertyValue(EDGE_PROPERTIES[0]) !== top) {
        style.setProperty(EDGE_PROPERTIES[0], top);
    }
    if (style.getPropertyValue(EDGE_PROPERTIES[1]) !== bottom) {
        style.setProperty(EDGE_PROPERTIES[1], bottom);
    }
    if (themeColor && themeColor.content !== top) themeColor.content = top;
    if (performance.now() < followUntil) refreshBrowserEdges();
}

export function refreshBrowserEdges() {
    if (!frame && owners.length > 0) frame = requestAnimationFrame(paint);
}

function follow() {
    followUntil = performance.now() + OVERLAY_SETTLE_MS;
    refreshBrowserEdges();
}

function registerOverlay(overlay: Overlay): () => void {
    const { element } = overlay;
    overlays.add(overlay);
    element.addEventListener("transitionstart", follow);
    element.addEventListener("animationstart", follow);
    follow();
    return () => {
        overlays.delete(overlay);
        element.removeEventListener("transitionstart", follow);
        element.removeEventListener("animationstart", follow);
        follow();
    };
}

function overlayRef(
    key: string,
    describe: (element: HTMLElement) => Overlay,
): RefCallback<HTMLElement> {
    let ref = overlayRefs.get(key);
    if (!ref) {
        ref = (element) =>
            element ? registerOverlay(describe(element)) : undefined;
        overlayRefs.set(key, ref);
    }
    return ref;
}

export function edgeScrimRef(
    tint: EdgeTint | null,
): RefCallback<HTMLElement> | undefined {
    return tint
        ? overlayRef(`scrim ${tint.color} ${tint.alpha}`, (element) => ({
              kind: "scrim",
              element,
              tint,
          }))
        : undefined;
}

export function edgePanelRef(
    color: Hex | null,
): RefCallback<HTMLElement> | undefined {
    return color
        ? overlayRef(`panel ${color}`, (element) => ({
              kind: "panel",
              element,
              color,
          }))
        : undefined;
}

function attach(): () => void {
    const root = document.documentElement;
    const meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.prepend(meta);
    themeColor = meta;
    root.setAttribute(ROOT_FLAG, "");
    const resize = new ResizeObserver(refreshBrowserEdges);
    resize.observe(document.body);
    window.addEventListener("scroll", refreshBrowserEdges, { passive: true });
    window.addEventListener("resize", refreshBrowserEdges);
    window.addEventListener("pageshow", refreshBrowserEdges);
    window.visualViewport?.addEventListener("resize", refreshBrowserEdges);
    window.visualViewport?.addEventListener("scroll", refreshBrowserEdges);
    return () => {
        cancelAnimationFrame(frame);
        frame = 0;
        resize.disconnect();
        window.removeEventListener("scroll", refreshBrowserEdges);
        window.removeEventListener("resize", refreshBrowserEdges);
        window.removeEventListener("pageshow", refreshBrowserEdges);
        window.visualViewport?.removeEventListener(
            "resize",
            refreshBrowserEdges,
        );
        window.visualViewport?.removeEventListener(
            "scroll",
            refreshBrowserEdges,
        );
        for (const property of EDGE_PROPERTIES) {
            root.style.removeProperty(property);
        }
        root.removeAttribute(ROOT_FLAG);
        meta.remove();
        themeColor = null;
    };
}

function claimEdges(owner: EdgeOwner): () => void {
    owners.push(owner);
    detach ??= attach();
    paint();
    return () => {
        const index = owners.indexOf(owner);
        if (index === -1) return;
        owners.splice(index, 1);
        if (owners.length > 0) {
            refreshBrowserEdges();
            return;
        }
        detach?.();
        detach = null;
    };
}

export function BrowserEdges({
    initial,
    paletteAt = () => initial,
    fade = false,
    layer = "page",
}: {
    initial: EdgePalette;
    paletteAt?: () => EdgePalette;
    fade?: boolean;
    layer?: EdgeLayer;
}) {
    const owner = useRef<EdgeOwner>({ layer, paletteAt });
    useUnsafeLayoutEffect(() => claimEdges(owner.current), []);
    useUnsafeLayoutEffect(() => {
        owner.current.layer = layer;
        owner.current.paletteAt = paletteAt;
        refreshBrowserEdges();
    });
    const topInitial = { "--edge-top-initial": initial.top } as CSSProperties;
    return (
        <>
            {fade ? (
                <div aria-hidden className="edge-top-fade" style={topInitial} />
            ) : null}
            <div aria-hidden className="edge-top" style={topInitial} />
            <div
                aria-hidden
                className="edge-bottom"
                style={
                    {
                        "--edge-bottom-initial": initial.bottom,
                    } as CSSProperties
                }
            />
        </>
    );
}
